import { authorByIdQuery, authorCreate, authorUpdate } from '@/entities/authors'
import type {
  AuthorInfoModel,
  AuthorModel,
  CreateOrUpdateAuthorModel,
} from '@/shared/api'
import { createModalModel } from '@/shared/lib/model-factories'
import { createEvent, sample } from 'effector'
import { createGate } from 'effector-react'
import { or } from 'patronum'

// Initialize

type AuthorFormGateProps = {
  isModal?: boolean
  onSubmitted?: (id: string) => void
}

export const AuthorFormGate = createGate<AuthorFormGateProps>()

// Modal

export const {
  modalOpened: authorFormModalOpened,
  modalClosed: authorFormModalClosed,
  $isModalOpened: $isAuthorFormModalOpened,
} = createModalModel()
const authorFormModalOpenedForEdit = createEvent<{ id: string }>()
sample({
  clock: authorFormModalOpened,
  filter: (params): params is { id: string } => Boolean(params),
  target: authorFormModalOpenedForEdit,
})

sample({
  clock: authorFormModalOpenedForEdit,
  target: authorByIdQuery.start,
})

sample({
  source: authorByIdQuery.$data,
  clock: authorFormModalClosed,
  filter: (author: AuthorModel | null): author is AuthorModel =>
    author !== null,
  target: authorByIdQuery.reset,
})

// Submit

export const $submittingAuthor = or(
  authorCreate.$pending,
  authorUpdate.$pending,
)

export const authorSubmitted = createEvent<CreateOrUpdateAuthorModel>()
sample({
  source: authorByIdQuery.$data,
  clock: authorSubmitted,
  filter: (author: AuthorModel | null): author is null => author === null,
  fn: (
    _,
    authorSubmittedData,
  ): {
    data: CreateOrUpdateAuthorModel
  } => ({ data: authorSubmittedData }),
  target: authorCreate.start,
})

sample({
  source: authorByIdQuery.$data,
  clock: authorSubmitted,
  filter: (author: AuthorInfoModel | null): author is AuthorInfoModel =>
    author !== null && typeof author.id === 'string',
  fn: (author, authorSubmittedData) => ({
    id: author.id,
    data: authorSubmittedData,
  }),
  target: authorUpdate.start,
})

// OnSubmitted

sample({
  clock: [authorCreate.finished.success, authorUpdate.finished.success],
  source: AuthorFormGate.state,
  filter: (gateState) => Boolean(gateState.isModal),
  fn: (gateState, { result }) => {
    if (result?.id && gateState.onSubmitted) {
      gateState.onSubmitted(result.id)
    }
  },
  target: authorFormModalClosed,
})
